'use strict';

/**
 * seedTemplateDefaults(fields, data) — copy an app-template's formly
 * `defaultValue` declarations into the app.data model.
 *
 * Why (Achievers "no data until I change the select", follows #561/#579):
 *
 * On the add screen (/app-store/:appTemplateId/add) edit-app.js sets
 * `this.app = null` and never initialises it; the model object only comes into
 * existence when formly renders and Angular's two-way bindings materialise it.
 * app-preview-wrapper, meanwhile, resolves the template, flips `loaded`, and
 * calls fetchDynamicForCurrentData() — which reads `_.get(this.app, 'data')`,
 * gets undefined, and returns early on shouldFetchDynamicForCurrentData()'s
 * `if (!data) return false` guard. No live feed is ever requested.
 *
 * The visible symptom is a select that already *reads* "Recognition" (its
 * declared defaultValue) sitting next to a preview saying "No Achievers
 * content", because the model behind that select is still empty. Choosing any
 * other option writes the model for the first time, the wrapper's
 * $watch('$ctrl.app.data') catches that write, and data finally appears —
 * which is why the app looks broken only until you touch the control.
 *
 * Seeding the declared defaults at template-load time makes the model agree
 * with what the form already displays, so the first preview decision sees
 * `whatToPlay: 'recognition'` and fetches straight away. This is generic over
 * every template's fields rather than an Achievers special case, matching the
 * "no per-slug hard-coding" direction of should-fetch-dynamic.js.
 *
 * Only fills gaps: a key the model already carries is never overwritten, so
 * this is safe to run over a partially-populated model and cannot clobber a
 * user's choice. Formly applies the same defaults itself once it renders; this
 * just stops the preview from having to wait for that.
 *
 * @param {Array|null|undefined} fields - appTemplate.fields.data (formly fields)
 * @param {object|null|undefined} data - the app.data model, mutated in place
 * @returns {object|null|undefined} the same `data` reference it was handed
 */
// Formly keys are paths, not flat names, and accept both dot and bracket/index
// syntax ('topLeft.type', 'items[0].type', "outer['inner'].leaf"). Splitting on
// dots alone would write a stray literal 'items[0]' property while the real
// model slot stayed unset — and that stray key is then saved with the app.
var PATH_TOKEN = /\[\s*(?:'([^']*)'|"([^"]*)"|(\d+))\s*\]|([^.[\]]+)/g;

// App Templates (including custom ones) are fetched from the API and their
// field definitions reach this helper before the app is ever saved, so a key
// like '__proto__.polluted' would otherwise walk straight onto Object.prototype
// and corrupt every object in the Manager runtime. Reject the dangerous
// segments outright rather than trying to sanitise them.
var UNSAFE_SEGMENTS = ['__proto__', 'constructor', 'prototype'];

// A segment addresses an array slot when it is a CANONICAL non-negative
// integer, independent of whether it was written with dots or brackets —
// 'items.0.type' and 'items[0].type' are the same path to formly/lodash, so
// they must build the same shape. Non-canonical spellings ('01', '-1', '1e2')
// are ordinary object keys, matching lodash's own _.set() behaviour.
function isIndexSegment(segment) {
    return /^(?:0|[1-9]\d*)$/.test(segment);
}

// Returns { segments, indexes } or null when the path is unusable/unsafe.
// `indexes[i]` marks a segment addressing an array slot, so the container
// created for it is an array rather than a plain object. Classification depends
// only on the segment's text, never on how it was written, which is what keeps
// the shape identical to the one lodash (and therefore formly) would build.
function parsePath(path) {
    var segments = [];
    var indexes = [];
    var match;

    PATH_TOKEN.lastIndex = 0;
    while ((match = PATH_TOKEN.exec(String(path))) !== null) {
        var quoted = match[1] !== undefined ? match[1] : match[2];
        var segment = quoted !== undefined
            ? quoted
            : (match[3] !== undefined ? match[3] : match[4].trim());

        segments.push(segment);
        indexes.push(isIndexSegment(segment));
    }

    if (!segments.length) return null;

    for (var i = 0; i < segments.length; i += 1) {
        if (!segments[i] || UNSAFE_SEGMENTS.indexOf(segments[i]) !== -1) return null;
    }

    return { segments: segments, indexes: indexes };
}

function hasPath(target, path) {
    var parsed = parsePath(path);
    if (!parsed) return true; // unsafe/unusable: treat as "already set" so it is never written

    var segments = parsed.segments;
    var cursor = target;

    for (var i = 0; i < segments.length; i += 1) {
        if (cursor === null || typeof cursor !== 'object') return false;
        if (!Object.prototype.hasOwnProperty.call(cursor, segments[i])) return false;
        cursor = cursor[segments[i]];
    }

    // An own property explicitly set to `undefined` is not a user choice — it is
    // an unset slot Angular or an earlier form render materialised. Report it as
    // missing so the declared default still lands; `null`, `false` and `0` are
    // real values and stay untouched.
    return cursor !== undefined;
}

// Walks to the container that should hold the final segment, creating plain
// objects (or arrays, for numeric bracket segments) along the way.
function resolveContainer(target, parsed) {
    var segments = parsed.segments;
    var cursor = target;

    for (var i = 0; i < segments.length - 1; i += 1) {
        var segment = segments[i];
        // Only descend through own properties: an inherited value would put the
        // cursor on a shared prototype.
        if (!Object.prototype.hasOwnProperty.call(cursor, segment)
            || cursor[segment] === null
            || typeof cursor[segment] !== 'object') {
            cursor[segment] = parsed.indexes[i + 1] ? [] : {};
        }
        cursor = cursor[segment];
    }

    return cursor;
}

// A structured defaultValue must never be shared with the schema: assigning it
// by reference means later form edits mutate appTemplate.fields[*].defaultValue
// itself, and that template object is stored in Store, so every later consumer
// would observe user-modified "defaults". Primitives (and null) are immutable
// and pass through untouched.
function cloneDefault(value) {
    if (value === null || typeof value !== 'object') return value;

    if (Array.isArray(value)) {
        return value.map(cloneDefault);
    }

    var copy = {};
    Object.keys(value).forEach(function (key) {
        if (UNSAFE_SEGMENTS.indexOf(key) !== -1) return;
        copy[key] = cloneDefault(value[key]);
    });
    return copy;
}

function setPath(target, path, value) {
    var parsed = parsePath(path);
    if (!parsed) return;

    var container = resolveContainer(target, parsed);
    container[parsed.segments[parsed.segments.length - 1]] = cloneDefault(value);
}

/**
 * @param {Array|null|undefined} fields - appTemplate.fields.data (formly fields)
 * @param {object|null|undefined} data - the app.data model, mutated in place
 * @returns {object|null|undefined} the same `data` reference it was handed
 */
function seedTemplateDefaults(fields, data) {
    if (!data || !Array.isArray(fields)) return data;

    fields.forEach(function (field) {
        if (!field) return;

        var hasDefault = field.key
            && Object.prototype.hasOwnProperty.call(field, 'defaultValue');

        // Apply the field's OWN default first. A keyed group may declare both a
        // default and children; resolving the child sub-model first would create
        // data.address = {}, after which hasPath() reports the key as present and
        // the declared group default is silently dropped.
        //
        // Presence rather than a truthiness check: an explicit `false`, `0` or
        // `null` already in the model is a real user choice, not an empty slot.
        // Plain path walking (no lodash) — these helpers are loaded both as an
        // Angular bundle global and bare under Mocha, where `_` is undefined.
        if (hasDefault && !hasPath(data, field.key)) {
            setPath(data, field.key, field.defaultValue);
        }

        // Nested groups declare their own children (e.g. corner settings). A
        // group that carries its OWN key scopes those children to that key's
        // sub-model — { key: 'address', fieldGroup: [{ key: 'city' }] } targets
        // data.address.city. Seeding children against the root instead would
        // leave a stray top-level data.city that formly never reconciles and
        // that is then saved with the app.
        if (Array.isArray(field.fieldGroup)) {
            seedTemplateDefaults(field.fieldGroup, resolveGroupModel(field, data));
        }
    });

    return data;
}

// A keyed fieldGroup's children are seeded against the group's own sub-model,
// which is created on demand. An unkeyed group shares the parent model, and an
// unsafe/unusable key yields null so the whole group is skipped. A group default
// applied above is preserved: only a non-object value is replaced.
function resolveGroupModel(field, data) {
    if (!field.key) return data;

    var parsed = parsePath(field.key);
    if (!parsed) return null;

    var container = resolveContainer(data, parsed);
    var leaf = parsed.segments[parsed.segments.length - 1];

    if (!Object.prototype.hasOwnProperty.call(container, leaf)
        || container[leaf] === null
        || typeof container[leaf] !== 'object') {
        container[leaf] = {};
    }

    return container[leaf];
}

// CommonJS export for Mocha unit tests (guarded so it is a no-op inside the
// concatenated Angular bundle, where `seedTemplateDefaults` is a plain global —
// mirrors the should-fetch-dynamic.js / limit-controls.js pattern).
if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = seedTemplateDefaults;
}

if (typeof angular !== 'undefined') {
    angular.module('rocket.components').value('seedTemplateDefaults', seedTemplateDefaults);
}
